/**
 * File: humanizeDescription.ts
 * Description: Turns raw knowledge-base document descriptions (stored as a
 *   JSON array of indexed content chunks full of escaped newlines and PDF
 *   layout glyphs) into a readable, single-line summary. When the cleaned
 *   text has too little real prose (e.g. a bulleted outline), an empty
 *   string is returned so the caller can hide the description entirely.
 * Author: GitHub Copilot
 * Created: 2026-06-01
 * Last Modified: 2026-06-01
 */

// A humanized description must contain at least this many letters/digits to
// be worth showing; otherwise it is just leftover bullets/layout glyphs.
const MIN_DESCRIPTION_LETTERS = 8;

/**
 * Convert a raw, machine-formatted document description into a clean,
 * human-readable single line. Returns an empty string when nothing
 * meaningful remains after cleanup.
 *
 * @param raw - The raw description payload from the knowledge base.
 * @returns A readable summary, or an empty string when not meaningful.
 */
export const humanizeDescription = (raw?: string | null): string => {
  if (!raw) {
    return '';
  }
  let text = raw;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      text = parsed.join(' ');
    } else if (typeof parsed === 'string') {
      text = parsed;
    }
  } catch {
    // Not JSON, keep the raw string as-is.
  }
  const cleaned = text
    .replace(/\\[nrt]/g, ' ') // literal escape sequences (\n, \r, \t)
    .replace(/[\r\n\t]+/g, ' ') // real control characters
    .replace(/[\u2022\u25aa\u25cf\u2580-\u25ff]/g, ' ') // bullets & glyphs
    .replace(/[[\]"]/g, ' ') // leftover JSON array punctuation
    .replace(/\s+/g, ' ')
    .trim();
  // If what's left is mostly punctuation/symbols (e.g. a bulleted outline
  // with no prose), there is nothing useful to show.
  const letterCount = (cleaned.match(/[a-z0-9]/gi) ?? []).length;
  return letterCount >= MIN_DESCRIPTION_LETTERS ? cleaned : '';
};
